import React, { useEffect, useState, useMemo } from 'react';
import { FileUpload, FormItem, SidePanel } from '@getsynapse/design-system';
import intl from 'react-intl-universal';
import { PickerFileMetadata } from 'filestack-js';
import { CreateFileUploadRequest, LineItemFile } from 'utils/customTypes';
import { get } from 'lodash';
import {
  deleteBudgetFile,
  fetchLineItemsFilesByLineItemId,
  saveBudgetFileUploadInfo,
} from 'state/BudgetList/budgetListSlice';
import { useDispatch } from 'react-redux';
import { getFileStackConfig } from 'utils/filestack';

export type Props = {
  open: boolean;
  onClose: () => void;
  lineItemId: string | undefined;
};

const AttachmentsSidePanel: React.FC<Props> = ({
  open,
  onClose,
  lineItemId,
}) => {
  const dispatch = useDispatch();
  const [fileUploadData, setFileUploadData] = useState<
    CreateFileUploadRequest[]
  >([]);

  const [displayFileData, setDisplayFileData] = useState<PickerFileMetadata[]>(
    []
  );

  const [selectedFiles, setSelectedFiles] = useState<LineItemFile[]>([]);
  const [storeToDeleteHandle, setStoreToDeleteHandle] = useState<
    string | null
  >();

  useEffect(() => {
    if (lineItemId && open) {
      fetchFiles(lineItemId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineItemId, open]);

  useEffect(() => {
    if (storeToDeleteHandle) {
      handleDeleteFile(storeToDeleteHandle);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeToDeleteHandle]);

  async function fetchFiles(lineItemId: string) {
    const response = await dispatch(
      fetchLineItemsFilesByLineItemId(lineItemId)
    );
    if (get(response, 'payload.data', null)) {
      const data = get(response, 'payload.data');
      setSelectedFiles([...data]);
      setDisplayFileData(
        data.map((file: LineItemFile) => ({
          filename: file.fileName,
          handle: file.handle,
          mimetype: file.fileType,
          url: file.fileUrl,
          originalPath: '',
          uploadId: '',
          source: '',
          size: 0,
        }))
      );
    }
  }

  function onFileUploadHandle(files: PickerFileMetadata[]) {
    if (files.length > 0) {
      const newFileDataList: CreateFileUploadRequest[] = files.map((file) => ({
        key: file?.key || '',
        fileName: file?.filename,
        fileSize: file?.size.toString(),
        fileUrl: file?.url,
        fileType: file?.mimetype,
        handle: file?.handle,
        lineItemId: lineItemId ?? '',
      }));
      setFileUploadData((prevData) => [...newFileDataList, ...prevData]);
    }
  }

  const onUploadedFileDataSave = async () => {
    const response = await dispatch(saveBudgetFileUploadInfo(fileUploadData));
    if (get(response, 'payload.data')) {
      setFileUploadData([]);
      onClose();
    }
  };

  async function onDelete(handle: string) {
    setStoreToDeleteHandle(handle);
  }

  const fileStackConfig = useMemo(() => getFileStackConfig(), []);
  async function handleDeleteFile(handle: string) {
    const fileToDelete = selectedFiles.find((item) => item.handle === handle);
    if (fileToDelete) {
      const response = await dispatch(deleteBudgetFile(handle));
      if (get(response, 'payload.data', null)) {
        setDisplayFileData((prevDisplayFileData) =>
          prevDisplayFileData.filter((file) => file.handle !== handle)
        );

        setStoreToDeleteHandle(null);
      }
    } else {
      setFileUploadData((prevData) =>
        prevData.filter((file) => file.handle !== handle)
      );
    }
  }

  return (
    <SidePanel
      footerButtons={[
        {
          children: intl.get('BUDGET_PAGE.ADD_ATTACHMENT_MODAL.CONFIRM'),
          variant: 'primary',
          color: 'default',
          onClick: async () => {
            onUploadedFileDataSave();
          },
        },
        {
          children: intl.get('CANCEL'),
          variant: 'tertiary',
          onClick: onClose,
        },
      ]}
      onClose={() => {
        onClose();
      }}
      isOpen={open}
    >
      <div>
        <div className='mb-4 font-bold text-xl'>
          {intl.get('BUDGET_PAGE.ADD_ATTACHMENT_MODAL.TITLE')}
        </div>
        <FormItem label={intl.get('BUDGET_PAGE.ADD_ATTACHMENT_MODAL.CAPTION')}>
          <div className='text-xs text-neutral mb-2'>
            {intl.get('BUDGET_PAGE.ADD_ATTACHMENT_MODAL.DESCRIPTION')}
          </div>
          <FileUpload
            config={fileStackConfig}
            files={displayFileData}
            onFileUploadHandle={onFileUploadHandle}
            onFileRemoveHandle={onDelete}
          />
        </FormItem>
      </div>
    </SidePanel>
  );
};

export default AttachmentsSidePanel;
